import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GlobalVariablesApi } from '@api/enterprise/openapi/endpoints/global-variables-api';
import type {
  GlobalVariableViewModel,
  UpdateGlobalVariableCommand,
  CreateGlobalVariableCommand
} from '@api/enterprise/openapi/models';
import { enterpriseApiClient } from '@api/enterprise/client';
import { globalVariablesKeys } from './globalVariable/constants';
import { useApiToast } from '@hooks/useApiToast';

// Query: Get global variable by name
export const useGlobalVariable = (name: string, enabled = true) => {
  const globalVariablesApi = new GlobalVariablesApi(
    undefined,
    undefined,
    enterpriseApiClient
  );

  return useQuery<GlobalVariableViewModel, Error>({
    queryKey: globalVariablesKeys.byName(name),
    queryFn: async () => {
      const response = await globalVariablesApi.apiGlobalVariablesNameGet({
        name
      });
      return response.data;
    },
    enabled: enabled && !!name,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000
  });
};

// Mutation: Update global variable
interface UpdateGlobalVariableParams {
  name: string;
  command: UpdateGlobalVariableCommand;
}

export const useUpdateGlobalVariable = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();
  const globalVariablesApi = new GlobalVariablesApi(
    undefined,
    undefined,
    enterpriseApiClient
  );

  return useMutation<
    GlobalVariableViewModel,
    Error,
    UpdateGlobalVariableParams
  >({
    mutationFn: async ({ name, command }) => {
      const response = await globalVariablesApi.apiGlobalVariablesNamePut({
        name,
        updateGlobalVariableCommand: command
      });
      return response.data;
    },

    onSuccess: (data, { name }) => {
      queryClient.setQueryData(globalVariablesKeys.byName(name), data);
      if (name === 'HIDE_PRECAL_CHECKLIST') {
        showSuccess('toast.saveSuccess', 'checklist');
      } else if (name === 'HIDE_ASSESSMENT_CONDITIONS') {
        showSuccess('equipmentAssessment.toast.saveSuccess', 'settings');
      } else if (name === 'HIDE_CUSTOMER_ACKNOWLEDGMENT') {
        showSuccess('customerAcknowledgment.toast.saveSuccess', 'settings');
      } else if (name === 'HIDE_CERT_TEMPLATE') {
        showSuccess('generalSettings.toast.saveSuccess', 'settings');
      } else if (name === 'HIDE_STATION_DEVICES') {
        // Toast handled by the station devices hook
      } else {
        showSuccess('allowedEvents.toast.saveSuccess', 'settings');
      }
    },

    onError: (error, { name }) => {
      if (name === 'HIDE_PRECAL_CHECKLIST') {
        showError('toast.saveError', error, 'checklist');
      } else if (name === 'HIDE_ASSESSMENT_CONDITIONS') {
        showError('equipmentAssessment.toast.saveError', error, 'settings');
      } else if (name === 'HIDE_CUSTOMER_ACKNOWLEDGMENT') {
        showError('customerAcknowledgment.toast.saveError', error, 'settings');
      } else if (name === 'HIDE_CERT_TEMPLATE') {
        showError('generalSettings.toast.saveError', error, 'settings');
      } else if (name === 'HIDE_STATION_DEVICES') {
        // Error handled by the station devices hook
      } else {
        showError('allowedEvents.toast.saveError', error, 'settings');
      }
    }
  });
};

// Mutation: Create global variable
export const useCreateGlobalVariable = () => {
  const queryClient = useQueryClient();
  const globalVariablesApi = new GlobalVariablesApi(
    undefined,
    undefined,
    enterpriseApiClient
  );

  return useMutation<
    GlobalVariableViewModel,
    Error,
    CreateGlobalVariableCommand
  >({
    mutationFn: async command => {
      const response = await globalVariablesApi.apiGlobalVariablesPost({
        createGlobalVariableCommand: command
      });
      return response.data;
    },
    onSuccess: (data, command) => {
      queryClient.setQueryData(globalVariablesKeys.byName(command.name), data);
    }
  });
};

// Export constants
export { globalVariablesKeys } from './globalVariable/constants';
